import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Sparkles, Loader2, Lightbulb, Wand2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AI-guided enrichment dialog for a weak stage chapter.
 *
 * Flow:
 *   1) Promoter clicks "Mejorar con IA" on a weak stage card.
 *   2) This dialog opens, fetches 5-7 personalized questions from Claude
 *      Haiku (POST /stages/{id}/enrichment-questions) reading the current
 *      chapter + form data + contributor anecdotes for that stage.
 *   3) Promoter answers as many or as few as they want.
 *   4) On Save, answers are stored (POST /stages/{id}/enrichment-answers),
 *      the stage chapter is added to ``stale_chapters``, the dialog closes,
 *      and the regen panel ("Hay capítulos pendientes de regenerar") fires
 *      on the Dashboard so the promoter can trigger the rewrite.
 */
export const StageEnrichmentDialog = ({ open, bookId, stage, onClose, onSaved }) => {
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentWords, setCurrentWords] = useState(null);
  const [targetWords, setTargetWords] = useState(3000);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!open || !stage) return;
    setQuestions([]);
    setAnswers({});
    setLoadingQuestions(true);
    axios
      .post(
        `${API_URL}/api/books/${bookId}/stages/${stage.stage_id}/enrichment-questions`,
        {},
        { withCredentials: true },
      )
      .then(({ data }) => {
        setQuestions(data.questions || []);
        setCurrentWords(data.current_words ?? null);
        setTargetWords(data.target_words ?? 3000);
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'No se pudieron generar las preguntas.');
        onClose?.();
      })
      .finally(() => setLoadingQuestions(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookId, stage?.stage_id]);

  const answeredCount = Object.values(answers).filter((v) => (v || '').trim().length > 0).length;

  const save = async () => {
    if (answeredCount === 0) {
      toast.error('Responde al menos una pregunta para mejorar el capítulo.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        answers: questions
          .map((q) => ({ question_id: q.question_id, answer: (answers[q.question_id] || '').trim() }))
          .filter((a) => a.answer.length > 0),
      };
      await axios.post(
        `${API_URL}/api/books/${bookId}/stages/${stage.stage_id}/enrichment-answers`,
        payload,
        { withCredentials: true },
      );
      toast.success(`Has añadido ${payload.answers.length} respuesta(s). El capítulo se marcará como pendiente de regenerar.`, { duration: 5000 });
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron guardar las respuestas.');
    } finally {
      setSaving(false);
    }
  };

  if (!stage) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="stage-enrichment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-600" />
            Mejorar el capítulo de "{stage.label}" con IA
          </DialogTitle>
          <DialogDescription className="space-y-1">
            <span className="block">
              La IA ha leído tu capítulo y los datos de los colaboradores y te propone preguntas concretas
              para añadir las palabras que faltan.
            </span>
            {currentWords !== null && (
              <span className="block text-xs text-muted-foreground">
                Estado actual: <strong>{currentWords}</strong> palabras de <strong>{targetWords}</strong> objetivo
                {' · '}
                Faltan ~<strong>{Math.max(targetWords - currentWords, 0)}</strong> palabras
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loadingQuestions ? (
          <div className="py-12 flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin" />
            <p className="text-sm">La IA está leyendo tu capítulo y preparando preguntas personalizadas…</p>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            {questions.map((q, i) => (
              <div key={q.question_id} className="rounded-lg border border-purple-100 bg-purple-50/30 p-3" data-testid={`enrichment-q-${q.question_id}`}>
                <div className="flex items-start gap-2 mb-2">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-medium flex items-center justify-center">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium leading-relaxed">{q.question}</p>
                    {q.hint && (
                      <p className="text-[11px] text-purple-700/80 mt-1 flex items-start gap-1">
                        <Lightbulb className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>{q.hint}</span>
                      </p>
                    )}
                  </div>
                </div>
                <Textarea
                  value={answers[q.question_id] || ''}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [q.question_id]: e.target.value }))}
                  placeholder="Responde con detalles concretos (puedes dejarla en blanco si no recuerdas)…"
                  className="min-h-[80px] text-sm resize-none"
                  data-testid={`enrichment-a-${q.question_id}`}
                />
              </div>
            ))}
            {questions.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">
                La IA no ha podido generar preguntas. Inténtalo de nuevo en unos minutos.
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="enrichment-cancel-btn">
            Cancelar
          </Button>
          <Button
            onClick={save}
            disabled={saving || loadingQuestions || answeredCount === 0}
            className="gap-2 bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="enrichment-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Guardar {answeredCount > 0 ? `${answeredCount} respuesta${answeredCount === 1 ? '' : 's'}` : ''} y marcar para regenerar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
